import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { RootState } from "../../store/store";
import apiClient from "../../api/apiClient";

export default function VerificationBanner() {
  const { t } = useTranslation();
  const userState = useSelector((state: RootState) => state.user.user);
  const [isDismissed, setIsDismissed] = useState(false);
  const [sending, setSending] = useState(false);

  // Extract user info
  const userObj = userState?.user || userState;
  const isEmailVerified = Boolean(
    userState?.isEmailVerified ?? userObj?.isEmailVerified ?? false
  );
  const isAdmin = Boolean(userState?.isAdmin ?? userObj?.isAdmin ?? false);
  const email = userObj?.email || "";

  // If already verified, admin, dismissed, or no user logged in, don't show
  if (!userObj || isEmailVerified || isAdmin || isDismissed) {
    return null;
  }

  const handleResend = async () => {
    if (!email) {
      toast.warn(t("verification.noEmailWarning", "Unable to detect your email address"));
      return;
    }

    try {
      setSending(true);
      const res = await apiClient.post("/api/v1/auth/resend-verification", {
        email,
      });

      toast.success(
        res.data?.message ||
          t(
            "verification.resendSuccess",
            "Verification email sent! Please check your inbox and spam folder."
          )
      );
    } catch (error: any) {
      console.error("Error resending verification:", error);
      toast.error(
        error.response?.data?.error?.message ||
          t("verification.resendError", "Failed to resend verification email")
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mb-6 rounded-2xl bg-amber-50/90 border border-amber-200/80 p-4 sm:p-4.5 shadow-xs transition-all duration-200">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <span className="text-xl shrink-0 mt-0.5" role="img" aria-label="warning">
            ⚠️
          </span>
          <div className="min-w-0 flex-1">
            <h4 className="text-sm font-bold text-amber-950">
              {t("verification.bannerTitle", "Email Verification Required")}
            </h4>
            <p className="text-xs sm:text-sm text-amber-900/80 mt-0.5 leading-relaxed">
              {t(
                "verification.bannerDesc",
                "Please verify your email address to ensure uninterrupted access. Your 48-hour grace period is currently active."
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 shrink-0 self-start sm:self-center">
          <button
            type="button"
            onClick={handleResend}
            disabled={sending}
            className="bg-[#037897] hover:bg-[#025f77] text-white text-xs sm:text-sm font-bold px-3.5 py-1.5 rounded-xl shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-60"
          >
            {sending ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{t("verification.sending", "Sending...")}</span>
              </>
            ) : (
              <>
                <span>✉️</span>
                <span>{t("verification.resendBtn", "Resend Link")}</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => setIsDismissed(true)}
            aria-label="Dismiss banner"
            className="text-amber-700/60 hover:text-amber-900 p-1.5 rounded-lg hover:bg-amber-100/60 transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
